import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const MenuContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const MenuTitle = styled(motion.h2)`
  color: #ff6b6b;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MenuItem = styled(motion.button)`
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.2rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 107, 107, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  color: #ff6b6b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 107, 107, 0.3);
    transform: scale(1.1);
  }
`;

const MoreMenuOverlay = ({ isOpen, onClose, onGetAppClick, onSpotifyClick, onYouTubeClick, onTalesClick }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayBackdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <MenuContainer
            initial={{ 
              opacity: 0, 
              scale: 0.8, 
              y: -100,
              rotateX: -15
            }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0,
              rotateX: 0
            }}
            exit={{ 
              opacity: 0, 
              scale: 0.8, 
              y: -100,
              rotateX: -15
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.6
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </CloseButton>
            
            <MenuTitle
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              🚀 More Options
            </MenuTitle>
            
            <MenuItems
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <MenuItem
                onClick={onGetAppClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                📱 Companion APP
              </MenuItem>
              
              <MenuItem
                onClick={onSpotifyClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <img 
                  src="/assets/spotifyLOGO.png" 
                  alt="Spotify" 
                  style={{ 
                    height: '20px', 
                    width: '20px'
                  }} 
                />
                Chikin Lady on SPOTIFY
              </MenuItem>
              
              <MenuItem
                onClick={onYouTubeClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.55 }}
              >
                <img 
                  src="/assets/youtubeLOGO.png" 
                  alt="YouTube" 
                  style={{ 
                    height: '20px', 
                    width: '20px'
                  }} 
                />
                Chikin Lady on YouTube
              </MenuItem>
              
              <MenuItem
                onClick={onTalesClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                📚 Tales of Chikin Lady
              </MenuItem>
            </MenuItems>
          </MenuContainer>
        </OverlayBackdrop>
      )}
    </AnimatePresence>
  );
};

export default MoreMenuOverlay;
